import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Suspense, useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { DonutShare } from '../../components/charts';
import { ExportMenu } from '../../components/ExportMenu';
import { FilterBar } from '../../components/FilterBar';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Pill, type PillTone } from '../../components/Pill';
import { Select } from '../../components/Select';
import { DataTable } from '../../components/Table';
import type { InventoryRow } from '../../data/fetchers';
import {
  getInventoryReport,
  listCategories,
  listMovements,
  listProducts,
} from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import {
  fmtAmount,
  fmtBahtShort,
  fmtInt,
  fmtStockBreakdown,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { ReportHeader } from './ReportHeader';

const TYPE_TH: Record<string, string> = {
  sale: 'ขายสินค้า',
  receive: 'รับสินค้าเข้า',
  adjust: 'ปรับปรุงสต๊อก',
  'transfer-in': 'รับโอนย้าย',
  'transfer-out': 'ส่งโอนย้าย',
};

const TYPE_TONE: Record<string, PillTone> = {
  sale: 'primary',
  receive: 'ok',
  adjust: 'warn',
  'transfer-in': 'ok',
  'transfer-out': 'danger',
};

interface InventoryExportRow {
  branchShort?: string;
  productId: string;
  name: string;
  category: string;
  onHandDisplay: string;
  onHandBaseUnits: number;
  valueBaht: string;
  valueSatang: number;
  status: string;
}

export function InventoryReportView() {
  const { scope, ready } = useBranchScope();

  // Filters State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Drill-down Modal State
  const [drillDownRow, setDrillDownRow] = useState<InventoryRow | null>(null);

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['report', 'inventory', scope.branchId, scope.allowedBranchIds],
    queryFn: () => getInventoryReport(scope),
    enabled: ready,
  });

  const showBranchCol = scope.branchId === null;

  const { data: movements = [] } = useQuery({
    queryKey: ['movements', scope.branchId, scope.allowedBranchIds],
    queryFn: () => listMovements(scope),
    enabled: ready && !!drillDownRow,
  });

  const rows = useMemo<InventoryRow[]>(() => data?.rows ?? [], [data]);

  // Filter logic
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      // 1. Search Query (SKU or Name)
      const q = searchQuery.trim().toLowerCase();
      if (
        q &&
        !r.name.toLowerCase().includes(q) &&
        !r.productId.toLowerCase().includes(q)
      ) {
        return false;
      }
      // 2. Category Filter
      if (selectedCategory !== 'all' && r.categoryId !== selectedCategory) {
        return false;
      }
      // 4. Stock Status Filter
      if (selectedStatus !== 'all') {
        const product = products.find((p) => p.id === r.productId);
        const minStock = product?.minStock ?? 0;
        const isOutOfStock = r.onHand <= 0;
        const isLowStock = r.onHand > 0 && r.onHand < minStock;

        if (selectedStatus === 'outOfStock' && !isOutOfStock) return false;
        if (selectedStatus === 'lowStock' && !isLowStock) return false;
        if (selectedStatus === 'normal' && (isOutOfStock || isLowStock))
          return false;
      }
      return true;
    });
  }, [
    rows,
    searchQuery,
    selectedCategory,
    selectedStatus,
    showBranchCol,
    products,
  ]);

  // Metrics derived from filtered list
  const metrics = useMemo(() => {
    const totalVal = filteredRows.reduce((s, r) => s + r.value, 0);
    const totalUnits = filteredRows.reduce((s, r) => s + r.onHand, 0);
    const lowCount = filteredRows.filter((r) => {
      const product = products.find((p) => p.id === r.productId);
      const minStock = product?.minStock ?? 0;
      return r.onHand > 0 && r.onHand < minStock;
    }).length;
    const outOfStockCount = filteredRows.filter((r) => r.onHand <= 0).length;

    return { totalVal, totalUnits, lowCount, outOfStockCount };
  }, [filteredRows, products]);

  // Donut 1: Value by Category
  const categoryDonutData = useMemo(() => {
    const byCategory = new Map<string, number>();
    for (const row of filteredRows) {
      if (row.value > 0) {
        byCategory.set(
          row.categoryId,
          (byCategory.get(row.categoryId) ?? 0) + row.value,
        );
      }
    }
    return [...byCategory.entries()].map(([categoryId, value]) => {
      const cat = categories.find((c) => c.id === categoryId);
      return { label: cat?.name ?? categoryId, value };
    });
  }, [filteredRows, categories]);

  // Donut 2: Value by Branch (if owner) OR Top 5 Products by Value (if single branch)
  const secondaryDonutData = useMemo(() => {
    if (showBranchCol) {
      const byBranch = new Map<string, number>();
      for (const row of filteredRows) {
        if (row.value > 0) {
          byBranch.set(
            row.branchShort,
            (byBranch.get(row.branchShort) ?? 0) + row.value,
          );
        }
      }
      return [...byBranch.entries()].map(([branchShort, value]) => ({
        label: branchShort,
        value,
      }));
    } else {
      const sorted = [...filteredRows].sort((a, b) => b.value - a.value);
      const top5 = sorted.slice(0, 5);
      const otherValue = sorted.slice(5).reduce((s, r) => s + r.value, 0);
      const data = top5.map((r) => ({ label: r.name, value: r.value }));
      if (otherValue > 0) {
        data.push({ label: 'อื่นๆ', value: otherValue });
      }
      return data;
    }
  }, [filteredRows, showBranchCol]);

  // Columns definition
  const columns = useMemo<ColumnDef<InventoryRow, unknown>[]>(() => {
    const cols: ColumnDef<InventoryRow, unknown>[] = [];

    if (showBranchCol) {
      cols.push({
        accessorKey: 'branchShort',
        header: 'สาขา',
        cell: (c) => (
          <span className={c.row.original.onHand <= 0 ? 'text-ink-4' : ''}>
            {c.getValue<string>()}
          </span>
        ),
      });
    }

    cols.push({
      accessorKey: 'productId',
      header: 'รหัสสินค้า',
      cell: (c) => (
        <span
          className={`num ${c.row.original.onHand <= 0 ? 'text-ink-4' : ''}`}
        >
          {c.getValue<string>()}
        </span>
      ),
    });

    cols.push({
      accessorKey: 'name',
      header: 'สินค้า',
      cell: (c) => (
        <span
          className={
            c.row.original.onHand <= 0
              ? 'text-ink-4 line-through'
              : 'font-semibold'
          }
        >
          {c.getValue<string>()}
        </span>
      ),
    });

    cols.push({
      accessorKey: 'categoryId',
      header: 'หมวดหมู่',
      cell: (c) => {
        const catId = c.getValue<string>();
        const catName = categories.find((x) => x.id === catId)?.name ?? catId;
        return (
          <span className={c.row.original.onHand <= 0 ? 'text-ink-4' : ''}>
            {catName}
          </span>
        );
      },
    });

    cols.push({
      accessorKey: 'onHand',
      header: 'คงเหลือ',
      cell: (c) => {
        const row = c.row.original;
        const product = products.find((p) => p.id === row.productId);
        const breakdown = product
          ? fmtStockBreakdown(row.onHand, product)
          : fmtInt(row.onHand);
        return (
          <span
            className={`num font-bold ${row.onHand <= 0 ? 'text-danger' : ''}`}
          >
            {breakdown}
          </span>
        );
      },
    });

    cols.push({
      accessorKey: 'value',
      header: 'มูลค่าสต๊อก (ทุน)',
      cell: (c) => (
        <span
          className={`num ${c.row.original.onHand <= 0 ? 'text-ink-4' : ''}`}
        >
          {fmtAmount(c.getValue<number>())}
        </span>
      ),
    });

    cols.push({
      id: 'status',
      header: 'สถานะ',
      cell: (c) => {
        const row = c.row.original;
        const product = products.find((p) => p.id === row.productId);
        const minStock = product?.minStock ?? 0;

        if (row.onHand <= 0) {
          return <Pill tone="danger">สินค้าหมด</Pill>;
        }
        if (row.onHand < minStock) {
          return <Pill tone="warn">สต๊อกต่ำ ({minStock})</Pill>;
        }
        return <Pill tone="ok">ปกติ</Pill>;
      },
    });

    return cols;
  }, [showBranchCol, products, categories]);

  const exportRows = useMemo<InventoryExportRow[]>(
    () =>
      filteredRows.map((row) => {
        const product = products.find((p) => p.id === row.productId);
        const minStock = product?.minStock ?? 0;
        const status =
          row.onHand <= 0
            ? 'สินค้าหมด'
            : row.onHand < minStock
              ? `สต๊อกต่ำ (${minStock})`
              : 'ปกติ';
        return {
          branchShort: row.branchShort,
          productId: row.productId,
          name: row.name,
          category:
            categories.find((category) => category.id === row.categoryId)
              ?.name ?? row.categoryId,
          onHandDisplay: product
            ? fmtStockBreakdown(row.onHand, product)
            : fmtInt(row.onHand),
          onHandBaseUnits: row.onHand,
          valueBaht: fmtAmount(row.value),
          valueSatang: row.value,
          status,
        };
      }),
    [filteredRows, products, categories],
  );

  const exportCols = useMemo<ExportCol<InventoryExportRow>[]>(() => {
    const base: ExportCol<InventoryExportRow>[] = [
      { key: 'productId', header: 'รหัสสินค้า' },
      { key: 'name', header: 'สินค้า' },
      { key: 'category', header: 'หมวดหมู่' },
      { key: 'onHandDisplay', header: 'คงเหลือ' },
      { key: 'onHandBaseUnits', header: 'คงเหลือ (base units)' },
      { key: 'valueBaht', header: 'มูลค่าสต๊อก (บาท)' },
      { key: 'valueSatang', header: 'มูลค่าสต๊อก (satang)' },
      { key: 'status', header: 'สถานะ' },
    ];
    if (showBranchCol) return [{ key: 'branchShort', header: 'สาขา' }, ...base];
    return base;
  }, [showBranchCol]);

  // Drill-down data
  const drillDownProduct = useMemo(() => {
    if (!drillDownRow) return null;
    return products.find((p) => p.id === drillDownRow.productId) ?? null;
  }, [products, drillDownRow]);

  const drillDownProductMovements = useMemo(() => {
    if (!drillDownRow) return [];
    return movements
      .filter(
        (m) =>
          m.productId === drillDownRow.productId &&
          m.branchId === drillDownRow.branchId,
      )
      .sort((a, b) => b.at - a.at);
  }, [movements, drillDownRow]);

  const activeFilterCount = [
    searchQuery.trim().length > 0,
    selectedCategory !== 'all',
    selectedStatus !== 'all',
  ].filter(Boolean).length;

  return (
    <div className="bo-fade space-y-6">
      <ReportHeader
        title="รายงานสต๊อก"
        subtitle="ประเมินมูลค่าตามต้นทุน (ต้นทุนเฉลี่ยเคลื่อนที่ × คงเหลือ) — ไม่ใช่ราคาขาย"
        status={{
          label:
            !ready || isLoading
              ? 'กำลังประมวลผล'
              : `${filteredRows.length} รายการ`,
          tone:
            !ready || isLoading
              ? 'neutral'
              : metrics.outOfStockCount > 0
                ? 'warn'
                : 'ok',
        }}
        actions={
          <ExportMenu
            rows={exportRows}
            cols={exportCols}
            filename="รายงานสต๊อก"
            title="รายงานสต๊อก"
            pdf
          />
        }
      />

      <section
        aria-labelledby="inventory-overview-heading"
        className="grid gap-4 lg:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]"
      >
        <div className="bo-card overflow-hidden bg-surface p-5 text-ink sm:p-6">
          <p
            id="inventory-overview-heading"
            className="text-sm-label font-bold uppercase tracking-[0.12em] text-ink-2"
          >
            มูลค่าสต๊อกตามตัวกรอง
          </p>
          <div className="mt-3 flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
            <strong className="num max-w-full break-words text-[clamp(1.5rem,7vw,3.5rem)] font-extrabold leading-none tracking-tight">
              {ready && data ? fmtAmount(metrics.totalVal) : '—'}
            </strong>
            <span className="text-body font-semibold text-ink-2">บาท</span>
          </div>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3 border-t border-white/10 pt-4">
            <div>
              <div className="text-sm-label text-ink-2">จำนวนคงเหลือ</div>
              <div className="mt-1 font-bold tabnum">
                {ready && data ? fmtInt(metrics.totalUnits) : '—'} ชิ้น
              </div>
            </div>
            <div>
              <div className="text-sm-label text-ink-2">รายการที่แสดง</div>
              <div className="mt-1 font-bold tabnum">
                {ready && data
                  ? `${filteredRows.length} / ${rows.length}`
                  : '—'}
              </div>
            </div>
          </div>
        </div>

        <div className="bo-card grid content-start gap-3 p-4 sm:p-5">
          <div>
            <h2 className="text-h3 font-bold text-ink">รายการที่ต้องตรวจสอบ</h2>
            <p className="mt-1 text-body text-ink-3">
              เลือกสถานะเพื่อกรองตารางทันที
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSelectedStatus('outOfStock')}
            className="flex min-h-14 items-center justify-between gap-3 rounded-btn border border-danger/20 bg-danger-light px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-danger/25"
          >
            <span className="font-bold text-danger">สินค้าหมด</span>
            <strong className="num text-h2 text-danger">
              {ready && data ? fmtInt(metrics.outOfStockCount) : '—'}
            </strong>
          </button>
          <button
            type="button"
            onClick={() => setSelectedStatus('lowStock')}
            className="flex min-h-14 items-center justify-between gap-3 rounded-btn border border-warn/20 bg-warn-light px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-warn/25"
          >
            <span className="font-bold text-warn">สต๊อกต่ำ</span>
            <strong className="num text-h2 text-warn">
              {ready && data ? fmtInt(metrics.lowCount) : '—'}
            </strong>
          </button>
        </div>
      </section>

      {/* Interactive Filters Panel */}
      <FilterBar>
        <div className="grid w-full gap-4">
          <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
            <div>
              <h2 className="text-h3 font-bold text-ink">ค้นหาในสต๊อก</h2>
              <p className="mt-1 text-body text-ink-3">
                แสดง {filteredRows.length} จาก {rows.length} รายการ
              </p>
            </div>
            {activeFilterCount > 0 && (
              <span className="w-fit rounded-chip bg-primary-light px-2.5 py-1 text-sm-label font-bold text-primary">
                ใช้ตัวกรอง {activeFilterCount} รายการ
              </span>
            )}
          </div>
          <div
            className={`grid min-w-0 gap-3 sm:grid-cols-2 ${showBranchCol ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}
          >
            <label className="flex min-w-0 flex-col gap-2">
              <span className="text-sm-label font-bold text-ink-2">
                ค้นหาสินค้า
              </span>
              <Input
                type="text"
                placeholder="รหัสสินค้า หรือ ชื่อสินค้า..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </label>

            <label className="flex min-w-0 flex-col gap-2">
              <span className="text-sm-label font-bold text-ink-2">
                หมวดหมู่
              </span>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">หมวดหมู่ทั้งหมด</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex min-w-0 flex-col gap-2">
              <span className="text-sm-label font-bold text-ink-2">
                สถานะสต๊อก
              </span>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">สถานะทั้งหมด</option>
                <option value="normal">ปกติ</option>
                <option value="lowStock">สต๊อกต่ำ</option>
                <option value="outOfStock">สินค้าหมด</option>
              </Select>
            </label>
          </div>

          {(searchQuery ||
            selectedCategory !== 'all' ||
            selectedStatus !== 'all') && (
            <Btn
              className="w-full sm:w-fit"
              kind="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setSelectedStatus('all');
              }}
            >
              ล้างตัวกรองทั้งหมด
            </Btn>
          )}
        </div>
      </FilterBar>

      <section aria-labelledby="inventory-list-heading" className="space-y-3">
        <div>
          <h2
            id="inventory-list-heading"
            className="text-h2 font-bold text-ink"
          >
            รายการคงเหลือ
          </h2>
          <p className="mt-1 text-body text-ink-3">
            เปิดประวัติของสินค้าแต่ละรายการเพื่อตรวจสอบที่มาของยอดคงเหลือ
          </p>
        </div>
        {!ready || isLoading ? (
          <div className="bo-card h-40 animate-pulse bg-bg-warm" />
        ) : (
          <DataTable
            pageSize={Number.MAX_SAFE_INTEGER}
            columns={columns}
            data={filteredRows}
            onRowClick={setDrillDownRow}
            emptyMessage="ไม่พบข้อมูลสินค้าตรงกับตัวกรองที่เลือก"
            mobileCards
          />
        )}
      </section>

      <section
        aria-labelledby="inventory-composition-heading"
        className="space-y-3"
      >
        <div>
          <h2
            id="inventory-composition-heading"
            className="text-h2 font-bold text-ink"
          >
            สัดส่วนมูลค่าสต๊อก
          </h2>
          <p className="mt-1 text-body text-ink-3">
            ดูว่ามูลค่าสต๊อกอยู่ที่หมวดหมู่ สาขา และสินค้าไหนมากที่สุด
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Card title="ตามหมวดหมู่">
            <Suspense
              fallback={
                <div
                  className="bo-card animate-pulse"
                  style={{ height: CHART_H.md }}
                />
              }
            >
              <DonutShare data={categoryDonutData} valueFormat={fmtBahtShort} />
            </Suspense>
          </Card>

          <Card title={showBranchCol ? 'ตามสาขา' : 'สินค้า Top 5 ตามมูลค่า'}>
            <Suspense
              fallback={
                <div
                  className="bo-card animate-pulse"
                  style={{ height: CHART_H.md }}
                />
              }
            >
              <DonutShare
                data={secondaryDonutData}
                valueFormat={fmtBahtShort}
              />
            </Suspense>
          </Card>
        </div>
      </section>

      {/* Stock History Drill-down Modal */}
      {drillDownRow && (
        <Modal
          variant="drawer"
          className="!my-0"
          open={!!drillDownRow}
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span>ประวัติการเคลื่อนไหวสต๊อก</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary-light text-primary">
                {drillDownRow.productId}
              </span>
            </span>
          }
          description={`ดูรายการรับเข้า ขาย และปรับปรุงสต๊อกของ ${drillDownRow.name} ที่สาขา ${drillDownRow.branchShort}`}
          onClose={() => setDrillDownRow(null)}
        >
          <div className="space-y-4">
            <div>
              <div className="text-body font-bold text-ink">
                {drillDownRow.name}
              </div>
              <div className="text-xs text-ink-3 mt-0.5">
                สาขา: {drillDownRow.branchShort}
              </div>
            </div>

            {/* Drilldown Summary Cards */}
            <div className="grid grid-cols-1 gap-3 rounded-card border border-line-2 bg-bg-warm/40 p-3 min-[420px]:grid-cols-3">
              <div className="text-center">
                <div className="text-sm-label font-bold text-ink-3 uppercase">
                  คงเหลือปัจจุบัน
                </div>
                <div className="text-sm font-extrabold text-ink mt-1">
                  {drillDownProduct
                    ? fmtStockBreakdown(drillDownRow.onHand, drillDownProduct)
                    : drillDownRow.onHand}
                </div>
              </div>
              <div className="border-y border-line-2 py-3 text-center min-[420px]:border-x min-[420px]:border-y-0 min-[420px]:py-0">
                <div className="text-sm-label font-bold text-ink-3 uppercase">
                  มูลค่าคงเหลือ (ทุน)
                </div>
                <div className="text-sm font-extrabold text-ink mt-1">
                  {fmtAmount(drillDownRow.value)} ฿
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm-label font-bold text-ink-3 uppercase">
                  การเคลื่อนไหว
                </div>
                <div className="text-sm font-extrabold text-ink mt-1">
                  {drillDownProductMovements.length} ครั้ง
                </div>
              </div>
            </div>

            <div className="max-h-[min(420px,50dvh)] overflow-y-auto rounded-card border border-line-2 bg-surface">
              {drillDownProductMovements.length === 0 ? (
                <div className="px-4 py-10 text-center text-body text-ink-3">
                  ไม่มีประวัติการเคลื่อนไหวในสาขานี้
                </div>
              ) : (
                <ol className="divide-y divide-line-2">
                  {drillDownProductMovements.map((movement) => (
                    <li
                      key={movement.id}
                      className="grid gap-2 p-3 sm:grid-cols-[132px_1fr_auto] sm:items-center sm:p-4"
                    >
                      <time className="num text-sm-label text-ink-3">
                        {new Date(movement.at).toLocaleString('th-TH', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </time>
                      <div className="min-w-0">
                        <Pill tone={TYPE_TONE[movement.type] ?? 'neutral'}>
                          {TYPE_TH[movement.type] ?? movement.type}
                        </Pill>
                        <p className="mt-1 break-words text-body text-ink-2">
                          {movement.reason ||
                            movement.note ||
                            'ไม่มีรายละเอียดเพิ่มเติม'}
                        </p>
                      </div>
                      <strong
                        className={`num text-left sm:text-right ${movement.qty < 0 ? 'text-danger' : 'text-ok'}`}
                      >
                        {movement.qty > 0 ? '+' : ''}
                        {movement.qty.toLocaleString()}
                      </strong>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
